import { NavLink, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import {
  LayoutDashboard,
  ShoppingCart,
  Ticket,
  Users,
  Store,
  Landmark,
  Building2,
  Gamepad2,
  ListOrdered,
  Gauge,
  Trophy,
  Banknote,
  BarChart3,
  Wallet,
  Shield,
  ShieldCheck,
  Settings as SettingsIcon,
  Smartphone,
  LogOut,
  Sparkles,
  Menu,
  X,
} from "lucide-react";
import { useAuth, ROLES, ROLE_GROUPS } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import api from "../../lib/api";

const { MANAGE, SUPERVISE, AUDIT, OPS, ALL, SUPER } = ROLE_GROUPS;

const NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, roles: ALL },
  { to: "/pos", label: "Punto de Venta", icon: ShoppingCart, roles: OPS },
  { to: "/payouts", label: "Pago de Premios", icon: Banknote, roles: OPS },
  { to: "/cash-sessions", label: "Turno de Caja", icon: Wallet, roles: OPS },
  { to: "/results", label: "Resultados", icon: Trophy, roles: SUPERVISE },
  { to: "/tickets", label: "Mis Tickets", icon: Ticket, roles: [...AUDIT, "cashier"] },
  { to: "/reports", label: "Reportes", icon: BarChart3, roles: AUDIT },
  { to: "/audit-logs", label: "Bitácora", icon: Shield, roles: AUDIT },
  { to: "/limits", label: "Límites de Números", icon: Gauge, roles: SUPERVISE },
  { to: "/users", label: "Usuarios", icon: Users, roles: MANAGE },
  { to: "/consortiums", label: "Consorcios", icon: Building2, roles: SUPER },
  { to: "/branches", label: "Sucursales", icon: Store, roles: MANAGE },
  { to: "/operators", label: "Operadores", icon: Landmark, roles: MANAGE },
  { to: "/games", label: "Juegos", icon: Gamepad2, roles: MANAGE },
  { to: "/play-types", label: "Tipos de Jugada", icon: ListOrdered, roles: MANAGE },
  { to: "/authorized-devices", label: "Dispositivos Autorizados", icon: ShieldCheck, roles: MANAGE },
  { to: "/settings", label: "Configuración", icon: SettingsIcon, roles: MANAGE },
  { to: "/sessions", label: "Mis Sesiones", icon: Smartphone, roles: ALL },
];

export default function AppShell({ children }) {
  const { user, logout, hasRole, activeConsortiumId, setActiveConsortiumId } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [consortiums, setConsortiums] = useState([]);

  // Super admin: load consortium list so they can pick the active scope.
  useEffect(() => {
    if (user?.role !== "super_admin") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/consortiums");
        if (cancelled) return;
        setConsortiums(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn("No se pudieron cargar consorcios", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  // Memoized nav items scoped to the current user's role — recomputed only when role/branch changes.
  const visibleNav = useMemo(
    () => NAV
      .filter((n) => hasRole(...n.roles))
      .filter((n) => n.to !== "/pos" || user?.branch_id),
    [user?.role, user?.branch_id]
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleConsortiumChange = (e) => {
    const cid = e.target.value;
    setActiveConsortiumId(cid);
    // Reload so every query/listing refetches with the new scope.
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-white flex text-slate-950">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setOpen(true)} className="p-1.5 -ml-1.5 rounded-sm hover:bg-slate-100" data-testid="mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-600 rounded-sm flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-black tracking-tight text-sm" style={{ fontFamily: "Manrope" }}>BANCA POS</span>
        </div>
        <div className="w-8" />
      </div>

      {/* Backdrop (mobile) */}
      {open && (
        <div className="lg:hidden fixed inset-0 bg-slate-950/50 z-40" onClick={() => setOpen(false)} data-testid="mobile-backdrop" />
      )}

      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 bg-slate-50 border-r border-slate-200 flex flex-col transition-transform`}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-sm flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-black text-slate-950 tracking-tight" style={{ fontFamily: "Manrope" }}>BANCA POS</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                Sistema de Bancas
              </div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden p-1 rounded-sm hover:bg-slate-200" data-testid="mobile-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.to.replace("/", "") || "dashboard"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-slate-200">
          {user?.role === "super_admin" && consortiums.length > 0 && (
            <div className="px-3 py-2 mb-2" data-testid="consortium-selector-wrap">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                Consorcio Activo
              </label>
              <select
                data-testid="consortium-selector"
                value={activeConsortiumId}
                onChange={handleConsortiumChange}
                className="w-full text-sm font-semibold bg-white border border-slate-300 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>Selecciona…</option>
                {consortiums.map((c) => (
                  <option key={c.id} value={c.id}>{`${c.code} — ${c.name}`}</option>
                ))}
              </select>
            </div>
          )}
          <div className="px-3 py-2 mb-2">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {ROLES[user?.role] || "Usuario"}
            </div>
            <div className="text-sm font-bold text-slate-900 truncate" data-testid="current-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2 rounded-sm border-slate-300 font-semibold"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col pt-14 lg:pt-0">{children}</main>
    </div>
  );
}
